import React, { useEffect, useState } from 'react';
import { Triad, getNoteDisplayName } from '../utils/noteUtils';
import './TriadDisplay.css';

interface TriadDisplayProps {
  currentTriad: Triad | null;
  isGameActive: boolean;
  completedNotes: number; // 0, 1, 2, or 3 (how many notes have been played correctly)
  flashColor?: 'green' | 'red' | null;
  isTransitioning?: boolean;
}

const TriadDisplay: React.FC<TriadDisplayProps> = ({ 
  currentTriad, 
  isGameActive, 
  completedNotes,
  flashColor, 
  isTransitioning 
}) => {
  const [isFlashing, setIsFlashing] = useState(false);

  useEffect(() => {
    if (flashColor) {
      setIsFlashing(true);
      setTimeout(() => setIsFlashing(false), 500);
    }
  }, [flashColor]);

  if (!isGameActive) {
    return (
      <div className="triad-display inactive">
        <p>Press Practice Triads to begin</p>
      </div>
    );
  }

  if (!currentTriad || isTransitioning) {
    return (
      <div className="triad-display active waiting">
        <div className="triad-info">
          <h2>Loading next triad...</h2>
        </div>
      </div>
    );
  }

  const { root, type, notes } = currentTriad;

  return (
    <div className={`triad-display active ${isFlashing ? `flash-${flashColor}` : ''}`}>
      <div className="triad-info">
        <h2>Play this triad:</h2>
        <div className="triad-name">
          {getNoteDisplayName(root)} {type.charAt(0).toUpperCase() + type.slice(1)}
        </div>
      </div>
      
      <div className="triad-notes">
        {notes.map((note, index) => {
          const isCompleted = index < completedNotes;
          const isCurrent = index === completedNotes;
          
          return (
            <div 
              key={index} 
              className={`note-circle ${
                isCompleted ? 'completed' : 
                isCurrent ? 'current' : 
                'upcoming'
              }`}
            >
              <div className="note-label">
                {index === 0 ? 'Root' : index === 1 ? '3rd' : '5th'}
              </div>
              <div className="note-name">
                {isCompleted || isCurrent ? getNoteDisplayName(note) : '?'}
              </div>
            </div>
          );
        })}
      </div>
      
      {completedNotes < 3 && (
        <div className="current-instruction">
          Play the {completedNotes === 0 ? 'root' : completedNotes === 1 ? '3rd' : '5th'} note
        </div>
      )}
    </div>
  );
};

export default TriadDisplay;